import React, { useMemo } from 'react';
import { StreamingImageTreeNodeData } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { hydrateRichTextVariables, renderRichTextToPlainString, normalizeStreamingRichText } from '../../richtext/ast';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';
import { cn } from '../../utils/cn';
import defaultImage from './images/default-image.png';

const PLACEHOLD_HOST = 'placehold.co';
const PLACEHOLD_QUERY_PARAM = 'mutinyPlaceholder';
const PLACEHOLD_HEX_COLOR_PATTERN = /^[0-9a-f]{3}([0-9a-f]{3})?$/i;
const PLACEHOLD_BORDER_WIDTH = 2;

const getPlaceholdBorderStyle = (src: string): React.CSSProperties | undefined => {
  try {
    const url = new URL(src);
    if (!url.searchParams.has(PLACEHOLD_QUERY_PARAM)) return undefined;
    if (url.hostname !== PLACEHOLD_HOST) return undefined;

    const segments = url.pathname.split('/').filter(Boolean);
    const fgColor = segments[2];

    if (!fgColor || fgColor.toLowerCase() === 'transparent') return undefined;

    const normalizedColor = fgColor.toLowerCase();
    const borderColor = PLACEHOLD_HEX_COLOR_PATTERN.test(normalizedColor) ? `#${normalizedColor}` : normalizedColor;

    return {
      border: `${PLACEHOLD_BORDER_WIDTH}px solid ${borderColor}`,
    };
  } catch {
    return undefined;
  }
};

type ImageProps = StreamingImageTreeNodeData & {
  id: string;
  editorClassName?: string;
  overrideVariables?: Partial<Record<string, string>>;
  staticAssetsBaseURL?: string | null;
  isEditor?: boolean;
  style?: React.CSSProperties;
};

export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      id,
      className,
      editorClassName,
      src,
      alt,
      overrideVariables,
      imageHref: href,
      openInNewTab,
      staticAssetsBaseURL,
      isEditor = false,
      style: styleProp,
      ...props
    },
    ref,
  ) => {
    // Normalize streaming RichText to complete RichText for safe processing
    const normalizedSrc = useMemo(() => normalizeStreamingRichText(src), [src]);
    const normalizedHref = useMemo(() => (href ? normalizeStreamingRichText(href) : undefined), [href]);

    const resolvedSrc = useMemo(() => {
      let value = normalizedSrc;

      // Hydrate RichText variables if needed
      if (typeof value !== 'string') {
        value = hydrateRichTextVariables(value, overrideVariables || {});
      }

      // Convert to plain string if still RichText
      if (typeof value !== 'string') {
        value = renderRichTextToPlainString(value);
      }

      // Prepend staticAssetsBaseURL if available and src is relative
      if (staticAssetsBaseURL && value && !value.startsWith('http')) {
        return `https://${staticAssetsBaseURL}${value}`;
      }

      return value;
    }, [normalizedSrc, overrideVariables, staticAssetsBaseURL]);

    const resolvedHref = useMemo(() => {
      if (!normalizedHref) return normalizedHref;

      let value = normalizedHref;

      // Hydrate RichText variables if needed
      if (typeof value !== 'string') {
        value = hydrateRichTextVariables(value, overrideVariables || {});
      }

      // Convert to plain string if still RichText
      if (typeof value !== 'string') {
        value = renderRichTextToPlainString(value);
      }

      return value;
    }, [normalizedHref, overrideVariables]);

    const onError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const eventTarget = event.target as HTMLImageElement;
      eventTarget.src = defaultImage;
      eventTarget.onerror = null;
    };

    const finalSrc = resolvedSrc || defaultImage;

    const imgStyle = useMemo(() => {
      if (!resolvedSrc) return styleProp;
      const placeholderStyle = getPlaceholdBorderStyle(resolvedSrc);
      if (!placeholderStyle) return styleProp;
      return {
        ...styleProp,
        ...placeholderStyle,
      };
    }, [resolvedSrc, styleProp]);

    const imgClassName = cn('max-w-full', transformToContainerQueries(className, { isEditor }), editorClassName);

    if (resolvedHref) {
      return (
        <a href={resolvedHref} target={openInNewTab ? '_blank' : '_self'} rel="noreferrer" className="contents">
          <img
            id={id}
            ref={ref}
            src={finalSrc}
            alt={alt}
            className={imgClassName}
            onError={onError}
            {...props}
            style={imgStyle}
          />
        </a>
      );
    }

    return (
      <img
        id={id}
        ref={ref}
        src={finalSrc}
        alt={alt}
        className={imgClassName}
        onError={onError}
        {...props}
        style={imgStyle}
      />
    );
  },
);

Image.displayName = 'Image';
